import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  BookOpen,
  Bell,
  ChevronsLeft,
  ChevronsRight,
  Command,
  LogOut,
  Menu,
  Search,
  Settings,
  X,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/auth/AuthProvider';
import { notificationsApi } from '@/api/endpoints';
import { queryKeys } from '@/api/queryKeys';
import { Button } from '@/design-system/Button';
import { PagePrefsProvider, TableRegistryProvider } from '@/hooks/usePagePrefs';
import { UserPreferencesProvider } from '@/hooks/useUserPreferences';
import { routeInLicensedModule } from './moduleAccess';
import { NAVIGATION } from './navigation';
import { CommandPalette } from './CommandPalette';
import { CreateMenu } from './CreateMenu';
import { ModuleSwitcher } from './modules';
import { UserMenu } from './UserMenu';
import { ThemeToggle } from './ThemeToggle';
import { Logo } from './Logo';

/**
 * The application shell.
 *
 * Responsibilities kept here rather than in each page: navigation, identity, theme, the command
 * palette, and the unread notification count. Pages render into the outlet and worry only about
 * their own subject.
 */
export function AppShell() {
  const { signOut, canAny, hasModule } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() =>
    typeof window !== 'undefined' ? localStorage.getItem('dam.sidebar') === 'collapsed' : false,
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // The mobile drawer must close on navigation, or the user taps a link and appears to stay put.
  useEffect(() => setMobileOpen(false), [location.pathname]);

  useEffect(() => {
    localStorage.setItem('dam.sidebar', collapsed ? 'collapsed' : 'expanded');
  }, [collapsed]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const visibleSections = NAVIGATION.map((section) => ({
    ...section,
    // Both questions: may this person do it, and did the company buy it.
    items: section.items.filter(
      (item) => canAny(item.anyOf) && routeInLicensedModule(item.to, hasModule),
    ),
  })).filter((section) => section.items.length > 0);

  return (
    // Preferences are per route, and the settings menu in the header has to see the columns of
    // whatever table the outlet renders - so both providers sit above the whole shell.
    <UserPreferencesProvider>
    <PagePrefsProvider>
      <TableRegistryProvider>
    <div className="flex h-full bg-background">
      {/* Skip link: the first stop for a keyboard user, so they can bypass the navigation. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-100 focus:rounded-md focus:bg-brand focus:px-4 focus:py-2 focus:text-white"
      >
        Skip to main content
      </a>

      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-overlay backdrop-blur-[2px] lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden
        />
      )}

      <Sidebar
        sections={visibleSections}
        collapsed={collapsed}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
        onToggleCollapse={() => setCollapsed((value) => !value)}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar
          onOpenMenu={() => setMobileOpen(true)}
          onOpenPalette={() => setPaletteOpen(true)}
          onSignOut={signOut}
        />

        <main id="main-content" className="app-backdrop flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
        </div>
      </TableRegistryProvider>
    </PagePrefsProvider>
    </UserPreferencesProvider>
  );
}

function Sidebar({
  sections,
  collapsed,
  mobileOpen,
  onCloseMobile,
  onToggleCollapse,
}: {
  sections: typeof NAVIGATION;
  collapsed: boolean;
  mobileOpen: boolean;
  onCloseMobile: () => void;
  onToggleCollapse: () => void;
}) {
  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-50 flex flex-col border-r border-border-subtle bg-surface transition-[width,transform] duration-200',
        'lg:static lg:translate-x-0',
        collapsed ? 'lg:w-16' : 'lg:w-64',
        'w-64',
        mobileOpen ? 'translate-x-0' : '-translate-x-full',
      )}
    >
      <div className="flex h-14 items-center gap-2.5 border-b border-border-subtle px-4">
        <Logo markOnly={collapsed} className={collapsed ? 'size-8' : 'h-8'} />
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={onCloseMobile}
          aria-label="Close navigation"
        >
          <X className="size-4" />
        </Button>
      </div>

      <nav aria-label="Main" className="flex-1 space-y-5 overflow-y-auto px-3 py-4">
        {sections.map((section) => (
          <div key={section.id}>
            {!collapsed && (
              <p className="px-2 pb-1.5 text-label text-text-muted">{section.label}</p>
            )}
            <ul className="space-y-0.5">
              {section.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    title={collapsed ? item.label : undefined}
                    className={({ isActive }) =>
                      cn(
                        'group flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-small font-medium transition-colors',
                        isActive
                          ? 'nav-active text-brand-text'
                          : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary',
                        collapsed && 'justify-center px-0',
                      )
                    }
                  >
                    <item.icon aria-hidden className="size-4.5 shrink-0" />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-t border-border-subtle p-3">
        <NavLink
          to="/user-manual"
          title={collapsed ? 'User manual' : undefined}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-small font-medium transition-colors',
              isActive
                ? 'nav-active text-brand-text'
                : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary',
              collapsed && 'lg:justify-center lg:px-0',
            )
          }
        >
          <BookOpen aria-hidden className="size-4.5 shrink-0" />
          <span className={cn('truncate', collapsed && 'lg:hidden')}>User manual</span>
        </NavLink>
      </div>

      <div className="hidden border-t border-border-subtle p-3 lg:block">
        <Button
          variant="ghost"
          size="sm"
          fullWidth={!collapsed}
          onClick={onToggleCollapse}
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          leadingIcon={
            collapsed ? <ChevronsRight className="size-4" /> : <ChevronsLeft className="size-4" />
          }
        >
          {!collapsed && 'Collapse'}
        </Button>
      </div>
    </aside>
  );
}

function TopBar({
  onOpenMenu,
  onOpenPalette,
  onSignOut,
}: {
  onOpenMenu: () => void;
  onOpenPalette: () => void;
  onSignOut: () => void;
}) {
  const { canAny } = useAuth();
  const canSeeNotifications = canAny(['NOTIFICATION_READ_OWN', 'NOTIFICATION_ADMIN']);

  const { data: unread } = useQuery({
    queryKey: queryKeys.notifications.unreadCount(),
    queryFn: notificationsApi.unreadCount,
    enabled: canSeeNotifications,
    refetchInterval: 60_000,
  });

  return (
    <header className="glass-bar top-0 z-30 flex h-14 shrink-0 items-center gap-3 border-b border-border-subtle px-4 sm:px-6">
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={onOpenMenu}
        aria-label="Open navigation"
      >
        <Menu className="size-5" />
      </Button>

      <BackButton />
      <ModuleSwitcher />

      <button
        type="button"
        onClick={onOpenPalette}
        className="group flex h-9 min-w-0 flex-1 items-center gap-2 rounded-lg border border-border-default bg-surface-sunken px-3 text-small text-text-muted transition-colors hover:border-border-strong hover:bg-surface-hover sm:max-w-md"
      >
        <Search aria-hidden className="size-4" />
        <span className="min-w-0 flex-1 truncate text-left">Search assets, people, orders…</span>
        <kbd className="hidden items-center gap-0.5 rounded border border-border-default bg-surface px-1.5 py-0.5 font-mono text-[0.6875rem] text-text-muted sm:inline-flex">
          <Command className="size-3" />K
        </kbd>
      </button>

      <div className="ml-auto flex items-center gap-1">
        <CreateMenu className="mr-1" />

        <ThemeToggle className="mr-1" />

        {canSeeNotifications && (
          <Link
            to="/notifications"
            className="relative grid size-9 place-items-center rounded-md text-text-secondary transition-colors hover:bg-surface-hover hover:text-text-primary"
            aria-label={
              unread?.unread ? `Notifications, ${unread.unread} unread` : 'Notifications'
            }
          >
            <Bell aria-hidden className="size-4.5" />
            {!!unread?.unread && (
              <span className="absolute top-1.5 right-1.5 grid min-w-4 place-items-center rounded-full bg-danger px-1 text-[0.625rem] font-semibold text-white">
                {unread.unread > 99 ? '99+' : unread.unread}
              </span>
            )}
          </Link>
        )}

        <div className="ml-1 flex items-center gap-2 border-l border-border-subtle pl-2">
          <UserMenu />
          <Link
            to="/settings"
            aria-label="Settings"
            title="Settings"
            className="grid size-9 place-items-center rounded-md text-text-secondary transition-colors hover:bg-surface-hover hover:text-text-primary"
          >
            <Settings aria-hidden className="size-4.5" />
          </Link>
          <Button variant="ghost" size="icon" onClick={onSignOut} aria-label="Sign out">
            <LogOut className="size-4.5" />
          </Button>
        </div>
      </div>
    </header>
  );
}

/**
 * Back, one step.
 *
 * Browser history when this tab has any inside the app; otherwise - a link opened in a fresh tab -
 * up one level of the address, so the button never leaves the application.
 */
function BackButton() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const hasHistory = (window.history.state as { idx?: number } | null)?.idx ?? 0;
  if (!hasHistory && pathname === '/dashboard') return null;

  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label="Go back"
      title="Go back"
      onClick={() => {
        if (hasHistory) navigate(-1);
        else navigate(pathname.split('/').slice(0, -1).join('/') || '/dashboard');
      }}
    >
      <ArrowLeft className="size-4.5" />
    </Button>
  );
}
